import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { PageHeader, Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import GoogleSheetsPanel from "@/components/GoogleSheetsPanel";
import {
  Archive, ArchiveRestore, Eye, EyeOff, Lock, Plus, Pencil, Search, Loader2, History, UploadCloud, Sparkles, Trash2, Quote, CakeSlice, X,
} from "lucide-react";
import RegistryBulkUploadDialog from "@/components/RegistryBulkUploadDialog";
import RegistryBirthdayImportDialog from "@/components/RegistryBirthdayImportDialog";
import FinanceDocumentsTab from "@/components/FinanceDocumentsTab";
import PublicBrandingTab from "@/components/PublicBrandingTab";
import GratitudeTab from "@/components/GratitudeTab";
import { toast } from "sonner";
import useBulkSelection from "@/hooks/useBulkSelection";

const TABS = [
  { key: "employees",  label: "Employees" },
  { key: "clients",    label: "Clients" },
  { key: "vendors",    label: "Vendors" },
  { key: "candidates", label: "Candidates" },
  { key: "gratitude",  label: "Gratitude" },
  { key: "finance-documents", label: "Finance Documents", restricted: true },
  { key: "public-branding", label: "Public Branding", requiresFeature: "branding_admin" },
];

const STAGE_OPTIONS = ["applied", "screening", "interview", "offer", "hired", "rejected"];

// Phase 170 — Registry mass selection + bulk edit. Deliberately narrow per
// tab: only categorical/common fields where "set the same value across many
// rows" is a sane operation (never name/email/statutory IDs). `status`
// mirrors the single-edit dialog's "Only Founders/Admins" gate via
// `requiresTeamEdit`; the backend enforces the identical rule independently.
const EMPLOYEE_ROLE_OPTIONS = ["admin", "hr", "finance", "client_manager", "designer", "employee"].map((r) => ({ value: r, label: r }));
const EMPLOYMENT_STATUS_OPTIONS = [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }];
const CANDIDATE_STAGE_SELECT_OPTIONS = STAGE_OPTIONS.map((s) => ({ value: s, label: s }));

const REGISTRY_BULK_FIELDS = {
  employees: [
    { key: "title", label: "Designation", type: "designation" },
    { key: "role", label: "Internal role", type: "select", options: EMPLOYEE_ROLE_OPTIONS },
    { key: "status", label: "Employment status", type: "select", options: EMPLOYMENT_STATUS_OPTIONS, requiresTeamEdit: true },
    { key: "email_muted", label: "Mute all emails", type: "boolean" },
  ],
  clients: [
    { key: "country", label: "Country", type: "text" },
    { key: "email_muted", label: "Mute all emails", type: "boolean" },
  ],
  vendors: [
    { key: "vendor_type", label: "Vendor Type", type: "vendor_type" },
    { key: "category", label: "Category", type: "text" },
    { key: "country", label: "Country", type: "text" },
  ],
  candidates: [
    { key: "candidate_stage", label: "Stage", type: "select", options: CANDIDATE_STAGE_SELECT_OPTIONS },
    { key: "candidate_source", label: "Source", type: "text" },
    { key: "candidate_role", label: "Role applied", type: "text" },
  ],
};

export default function AdminRegistry() {
  const { user } = useAuth();
  const can = usePermissions();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "employees";
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState(null);     // row or {} for new
  const [revealed, setRevealed] = useState({});      // {rowId: {pan,aadhaar,...}}
  const [loading, setLoading] = useState(true);
  const [costHistoryFor, setCostHistoryFor] = useState(null);  // employee row
  const [bulkOpen, setBulkOpen] = useState(false);
  const [birthdayImportOpen, setBirthdayImportOpen] = useState(false);

  // Gate: admin, founder, hr, or anyone specifically granted Manage Public Branding
  useEffect(() => {
    if (user && !["admin", "founder", "hr"].includes(user.role) && !can("branding_admin")) navigate("/");
  }, [user, navigate, can]);

  const load = useCallback(async () => {
    if (tab === "finance-documents" || tab === "public-branding") {
      // Still fetch /registry/summary so the OTHER tabs' active/archived
      // counts in the tab bar stay accurate while this tab is open.
      setLoading(false);
      try { const { data } = await api.get(`/registry/summary`); setSummary(data || {}); } catch { /* non-fatal */ }
      return;
    }
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        api.get(`/registry/${tab}`, { params: { include_archived: includeArchived, q: q || undefined } }),
        api.get(`/registry/summary`),
      ]);
      setRows(list.data || []);
      setSummary(sum.data || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab, includeArchived, q]);

  useEffect(() => { load(); }, [load]);

  const reveal = async (row) => {
    if (revealed[row.id]) {
      const next = { ...revealed };
      delete next[row.id];
      setRevealed(next);
      return;
    }
    try {
      const { data } = await api.post(`/registry/${tab}/${row.id}/reveal`);
      setRevealed({ ...revealed, [row.id]: data });
      toast.success("Statutory values revealed (audit-logged)");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const archive = async (row) => {
    if (!window.confirm(`Archive "${row.name || row.email}"? You can restore later — nothing is deleted.`)) return;
    try {
      await api.post(`/registry/${tab}/${row.id}/archive`);
      toast.success("Archived");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restore = async (row) => {
    try {
      await api.post(`/registry/${tab}/${row.id}/restore`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sumFor = (k) => summary[k] || { active: 0, archived: 0 };

  const isAdminOrFounder = can("finance_create") || can("finance_edit") || can("finance_delete");
  const isFounder = user?.role === "founder";
  const canEditEmployment = can("team_edit");
  // Phase 170 — mirrors the backend's `_registry_assert_roster_edit`: the
  // employees tab needs a roster-edit action; clients/vendors/candidates
  // only need the registry_view that got this page to load at all.
  const canBulkEditEmployees = can("registry_manage_roster") || can("team_create") || can("team_edit") || can("team_delete");
  const canBulkEditTab = tab === "employees" ? canBulkEditEmployees : true;
  // Feb 2026 — Public Branding is delegable via the "Manage Public
  // Branding" toggle (previously hardcoded founder-only).
  const visibleTabs = TABS.filter((t) => (!t.restricted || isAdminOrFounder) && (!t.requiresFeature || can(t.requiresFeature)));
  const isFinanceDocsTab = tab === "finance-documents";
  const isPublicBrandingTab = tab === "public-branding";
  const isGratitudeTab = tab === "gratitude";
  const isCustomTab = isFinanceDocsTab || isPublicBrandingTab || isGratitudeTab;

  // Phase 137 — Team Registry auto-sort: Active first, then On Notice
  // (status still "active" but a last_working_day is on file), Inactive /
  // Resigned always at the bottom. Scoped strictly to the `employees` tab
  // display order — never touches Access Management's own row ordering.
  const displayRows = useMemo(() => {
    if (tab !== "employees") return rows;
    const rank = (r) => (r.status === "inactive" ? 2 : r.last_working_day ? 1 : 0);
    return [...rows].sort((a, b) => rank(a) - rank(b));
  }, [rows, tab]);

  return (
    <div className="min-h-full bg-slate-50 p-4 sm:p-8 max-w-7xl mx-auto fade-in">
      <PageHeader
        eyebrow="Phase 33"
        title="Registry"
        subtitle="The single source-of-truth for People & Organisations. Statutory IDs are encrypted at rest, masked in the UI, and revealed only with an audit-logged click."
        actions={
          isCustomTab ? null : (
            <div className="flex items-center gap-2">
              {["clients", "vendors"].includes(tab) && (
                <Button
                  variant="outline"
                  onClick={() => setBulkOpen(true)}
                  className="rounded-sm border-[#F54800] text-[#F54800] hover:bg-[#F54800]/5"
                  data-testid="registry-bulk-upload-open"
                >
                  <UploadCloud size={13} className="mr-1.5" />Bulk upload
                </Button>
              )}
              {tab === "employees" && isFounder && (
                <Button
                  variant="outline"
                  onClick={() => setBirthdayImportOpen(true)}
                  className="rounded-sm border-[#F54800] text-[#F54800] hover:bg-[#F54800]/5"
                  data-testid="registry-birthday-import-open"
                >
                  <CakeSlice size={13} className="mr-1.5" />Import birthdays &amp; anniversaries
                </Button>
              )}
              <Button onClick={() => setEditing({})} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="registry-new">
                <Plus size={13} className="mr-1.5" />New {TABS.find((t) => t.key === tab)?.label.replace(/s$/, "").toLowerCase()}
              </Button>
            </div>
          )
        }
      />

      <div className="space-y-4 mt-4">
        {/* Google Sheets panel sits above the tabs */}
        {!isCustomTab && <GoogleSheetsPanel />}

        {tab === "clients" && isFounder && <ClientQuotePoolPanel />}

        {/* Tab bar with active/archived counts */}
        <div className="flex flex-wrap items-center gap-1 border-b border-slate-200" data-testid="registry-tabs">
          {visibleTabs.map((t) => {
            const s = sumFor(t.key);
            return (
              <button
                key={t.key}
                onClick={() => setParams({ tab: t.key })}
                className={`px-4 py-2.5 text-xs font-medium border-b-2 -mb-px ${
                  tab === t.key
                    ? "border-[#F54800] text-[#F54800]"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
                data-testid={`registry-tab-${t.key}`}
              >
                {t.label}
                {t.key !== "finance-documents" && t.key !== "public-branding" && t.key !== "gratitude" && (
                  <span className="ml-2 text-[10px] text-slate-400 font-normal">
                    {s.active}{s.archived ? ` · ${s.archived} archived` : ""}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {isFinanceDocsTab ? (
          <FinanceDocumentsTab />
        ) : isPublicBrandingTab ? (
          <PublicBrandingTab />
        ) : isGratitudeTab ? (
          <GratitudeTab isFounder={user?.role === "founder"} />
        ) : (
          <>
            {/* Search + show-archived */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name, email, company…"
                  className="rounded-sm pl-8 h-9 text-xs"
                  data-testid="registry-search"
                />
              </div>
              <label className="text-[11px] text-slate-600 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                  className="rounded-sm"
                  data-testid="registry-include-archived"
                />
                Include archived
              </label>
            </div>

            {/* Table */}
            <div className="card-flat overflow-hidden" data-testid="registry-table">
              {loading ? (
                <div className="p-10 text-center text-xs text-slate-400 inline-flex items-center gap-2 w-full justify-center">
                  <Loader2 size={12} className="animate-spin" /> Loading…
                </div>
              ) : rows.length === 0 ? (
                <div className="p-10 text-center text-sm text-slate-500">Nothing here yet.</div>
              ) : (
                <RegistryTable
                  tab={tab}
                  rows={displayRows}
                  revealed={revealed}
                  isFounder={user?.role === "founder"}
                  canBulkEditTab={canBulkEditTab}
                  canEditEmployment={canEditEmployment}
                  onReveal={reveal}
                  onEdit={(r) => setEditing(r)}
                  onArchive={archive}
                  onRestore={restore}
                  onShowCostHistory={(r) => setCostHistoryFor(r)}
                  onReload={load}
                />
              )}
            </div>
          </>
        )}
      </div>

      {editing !== null && (
        <RegistryEditDialog
          tab={tab}
          row={editing}
          canEditEmployment={can("team_edit")}
          isFounder={user?.role === "founder"}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {costHistoryFor && (
        <SeatCostHistoryDialog row={costHistoryFor} onClose={() => setCostHistoryFor(null)} />
      )}
      {bulkOpen && (
        <RegistryBulkUploadDialog tab={tab} onClose={() => setBulkOpen(false)} onDone={load} />
      )}
      {birthdayImportOpen && (
        <RegistryBirthdayImportDialog onClose={() => setBirthdayImportOpen(false)} onDone={load} />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function RegistryTable({ tab, rows, revealed, isFounder, canBulkEditTab, canEditEmployment, onReveal, onEdit, onArchive, onRestore, onShowCostHistory, onReload }) {
  const cols = (COLS_BY_TAB[tab] || []).filter((c) => c.key !== "seat_cost_monthly" || isFounder);
  // Rows come from different underlying collections depending on the tab —
  // wire the bulk hook accordingly. `employees` maps to the bare `users`
  // collection which is NOT bulk-deletable (would nuke admins too), so the
  // hook is silently disabled for that tab.
  const collectionByTab = {
    vendors: "users_vendor",
    clients: "users_client",
    candidates: "candidates",
  };
  const collection = collectionByTab[tab];
  const bulk = useBulkSelection({
    collection: collection || "__none__",  // deliberately invalid — backend 400s if ever hit; hook is hidden below when no collection
    ids: rows.map((r) => r.id),
    onDone: () => window.location.reload(),
  });
  // Phase 170 — mass selection + bulk edit. The checkbox column is now
  // shared by two independent capabilities: Bulk Edit (gated per-tab by
  // `canBulkEditTab`, available to Admin/HR too) and Bulk Delete (still
  // Founder-only via the pre-existing `useBulkSelection` hook). Either one
  // being available is enough to show the column — the action bar below
  // then shows only the button(s) the current user actually has.
  const canBulkDeleteThisTab = !!collection && bulk.canBulk;
  const showCheckboxes = canBulkEditTab || canBulkDeleteThisTab;
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead className="bg-slate-50">
          <tr>
            {showCheckboxes && (
              <th className="w-8 px-2 py-2.5">
                <Checkbox
                  checked={bulk.allSelected}
                  onCheckedChange={bulk.toggleAll}
                  aria-label="Select all rows"
                  data-testid="registry-bulk-header-checkbox"
                  onClick={(e) => e.stopPropagation()}
                />
              </th>
            )}
            {cols.map((c) => (
              <th key={c.key} className="text-left px-3 py-2.5 font-medium text-slate-500 uppercase tracking-wider text-[10px]">
                {c.label}
              </th>
            ))}
            <th className="text-right px-3 py-2.5 text-[10px] font-medium text-slate-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => {
            const isArchived = r.lifecycle_state === "archived";
            const rev = revealed[r.id] || null;
            return (
              <tr key={r.id} className={`hover:bg-slate-50 ${isArchived ? "opacity-50" : ""} ${showCheckboxes && bulk.isSelected(r.id) ? "bg-orange-50/40" : ""}`} data-testid={`registry-row-${r.id}`}>
                {showCheckboxes && (
                  <td className="w-8 px-2 py-2">
                    <Checkbox
                      checked={bulk.isSelected(r.id)}
                      onCheckedChange={() => {}}
                      aria-label={`Select row ${r.id}`}
                      data-testid={`registry-bulk-row-${r.id}`}
                      onClick={(e) => { e.stopPropagation(); bulk.toggle(r.id, { shiftKey: e.shiftKey }); }}
                    />
                  </td>
                )}
                {cols.map((c) => (
                  <td key={c.key} className="px-3 py-2 text-slate-700 align-top">
                    <RenderCell row={r} col={c} reveal={rev} onShowCostHistory={onShowCostHistory} />
                  </td>
                ))}
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-0.5">
                    {hasStatutoryValues(r) && (
                      <button
                        onClick={() => onReveal(r)}
                        className="p-1.5 text-slate-400 hover:text-[#F54800]"
                        title={rev ? "Hide statutory values" : "Reveal statutory values (audit-logged)"}
                        data-testid={`registry-reveal-${r.id}`}
                      >
                        {rev ? <EyeOff size={12} /> : <Eye size={12} />}
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(r)}
                      className="p-1.5 text-slate-400 hover:text-slate-700"
                      title="Edit"
                      data-testid={`registry-edit-${r.id}`}
                    >
                      <Pencil size={12} />
                    </button>
                    {isArchived ? (
                      <button
                        onClick={() => onRestore(r)}
                        className="p-1.5 text-slate-400 hover:text-green-600"
                        title="Restore"
                        data-testid={`registry-restore-${r.id}`}
                      >
                        <ArchiveRestore size={12} />
                      </button>
                    ) : (
                      <button
                        onClick={() => onArchive(r)}
                        className="p-1.5 text-slate-400 hover:text-orange-600"
                        title="Archive (never deletes)"
                        data-testid={`registry-archive-${r.id}`}
                      >
                        <Archive size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {showCheckboxes && (
        <RegistryBulkActionBar
          count={bulk.selected.size}
          canBulkEdit={canBulkEditTab}
          canBulkDelete={canBulkDeleteThisTab}
          busy={bulk.busy}
          onBulkEditClick={() => setBulkEditOpen(true)}
          onDeleteClick={bulk.openConfirm}
          onClear={bulk.clear}
        />
      )}
      {canBulkDeleteThisTab && <bulk.ConfirmDialog />}
      {bulkEditOpen && (
        <RegistryBulkEditDialog
          tab={tab}
          ids={Array.from(bulk.selected)}
          canEditEmployment={canEditEmployment}
          onClose={() => setBulkEditOpen(false)}
          onDone={() => { setBulkEditOpen(false); bulk.clear(); onReload && onReload(); }}
        />
      )}
    </div>
  );
}

function RegistryBulkActionBar({ count, canBulkEdit, canBulkDelete, busy, onBulkEditClick, onDeleteClick, onClear }) {
  if (count === 0) return null;
  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 bg-slate-900 text-white rounded-full shadow-lg border border-slate-800"
      data-testid="registry-bulk-action-bar"
    >
      <div className="text-sm font-medium" data-testid="registry-bulk-selection-count">{count} selected</div>
      <div className="w-px h-4 bg-slate-700" />
      {canBulkEdit && (
        <Button
          size="sm"
          onClick={onBulkEditClick}
          className="bg-white/10 hover:bg-white/20 text-white h-8 rounded-full"
          data-testid="registry-bulk-edit-btn"
        >
          <Pencil size={13} className="mr-1.5" />Bulk edit
        </Button>
      )}
      {canBulkDelete && (
        <Button
          size="sm"
          onClick={onDeleteClick}
          disabled={busy}
          className="bg-[#F54800] hover:bg-[#d63f00] text-white h-8 rounded-full"
          data-testid="registry-bulk-delete-btn"
        >
          {busy ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Trash2 size={13} className="mr-1.5" />}
          Delete Selected
        </Button>
      )}
      <button onClick={onClear} className="text-slate-400 hover:text-white p-1 rounded-full" data-testid="registry-bulk-clear-btn" aria-label="Clear selection">
        <X size={14} />
      </button>
    </div>
  );
}

function BulkValueInput({ field, value, onChange, designations, vendorTypes }) {
  if (!field) return null;
  if (field.type === "boolean") {
    return (
      <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="bulk-edit-value-boolean">
        <Checkbox
          checked={!!value}
          onCheckedChange={(v) => onChange(!!v)}
          className="data-[state=checked]:bg-[#F54800] data-[state=checked]:border-[#F54800]"
        />
        Enabled
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="rounded-sm" data-testid="bulk-edit-value-select"><SelectValue placeholder="Pick a value" /></SelectTrigger>
        <SelectContent>
          {field.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "designation") {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="rounded-sm" data-testid="bulk-edit-value-select"><SelectValue placeholder="Pick a designation" /></SelectTrigger>
        <SelectContent>
          {designations.length === 0 && <div className="px-2 py-1 text-xs text-slate-400">No designations yet — add one in Studio &gt; Taxonomy</div>}
          {designations.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "vendor_type") {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="rounded-sm" data-testid="bulk-edit-value-select"><SelectValue placeholder="Pick a type" /></SelectTrigger>
        <SelectContent>
          {vendorTypes.length === 0 && <div className="px-2 py-1 text-xs text-slate-400">No types yet — add one in Studio &gt; Taxonomy</div>}
          {vendorTypes.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  return <Input value={value} onChange={(e) => onChange(e.target.value)} className="rounded-sm" placeholder="New value…" data-testid="bulk-edit-value-text" />;
}

function RegistryBulkEditDialog({ tab, ids, canEditEmployment, onClose, onDone }) {
  const fields = useMemo(
    () => (REGISTRY_BULK_FIELDS[tab] || []).filter((f) => !f.requiresTeamEdit || canEditEmployment),
    [tab, canEditEmployment]
  );
  const [fieldKey, setFieldKey] = useState(fields[0]?.key || "");
  const [value, setValue] = useState("");
  const [designations, setDesignations] = useState([]);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tab === "employees") api.get("/taxonomy/lists/designations").then(({ data }) => setDesignations(data?.values || [])).catch(() => {});
    if (tab === "vendors") api.get("/taxonomy/lists/vendor_types").then(({ data }) => setVendorTypes(data?.values || [])).catch(() => {});
  }, [tab]);

  const activeField = fields.find((f) => f.key === fieldKey);
  const changeField = (k) => { setFieldKey(k); setValue(""); };

  const valueIsEmpty = activeField && activeField.type !== "boolean" && (value === "" || value == null);

  const apply = async () => {
    setSaving(true);
    try {
      await api.post(`/registry/${tab}/bulk-update`, { ids, field: fieldKey, value });
      toast.success(`Updated ${ids.length} record${ids.length === 1 ? "" : "s"}`);
      setConfirmOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const valueLabel = () => {
    if (!activeField) return "";
    if (activeField.type === "boolean") return value ? "On" : "Off";
    if (activeField.type === "select") return activeField.options.find((o) => o.value === value)?.label || String(value);
    return value || "(blank)";
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="rounded-sm max-w-md" data-testid="registry-bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Bulk edit — {ids.length} selected</DialogTitle>
            <DialogDescription className="text-xs">
              Choose a field and a new value to apply to every selected {tab.replace(/s$/, "")}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Field to change">
              <Select value={fieldKey} onValueChange={changeField}>
                <SelectTrigger className="rounded-sm" data-testid="bulk-edit-field-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {fields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="New value">
              <BulkValueInput field={activeField} value={value} onChange={setValue} designations={designations} vendorTypes={vendorTypes} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={!fieldKey || valueIsEmpty}
              className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white"
              data-testid="bulk-edit-review-btn"
            >
              Review &amp; apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirmOpen} onOpenChange={(o) => { if (!saving) setConfirmOpen(o); }}>
        <AlertDialogContent className="rounded-sm" data-testid="bulk-edit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm bulk edit</AlertDialogTitle>
            <AlertDialogDescription>
              Set <strong>{activeField?.label}</strong> to <strong>{valueLabel()}</strong> on {ids.length} selected {tab}? This cannot be undone automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-edit-cancel-btn" disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); apply(); }}
              disabled={saving}
              className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white"
              data-testid="bulk-edit-confirm-btn"
            >
              {saving ? <><Loader2 size={13} className="animate-spin mr-1.5" />Applying…</> : "Apply"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function hasStatutoryValues(r) {
  return r.pan_has_value || r.aadhaar_has_value || r.bank_account_has_value || r.passport_has_value || r.driving_licence_has_value;
}

function RenderCell({ row, col, reveal, onShowCostHistory }) {
  const k = col.key;
  if (k === "lifecycle_state") {
    return <Tag tone={row.lifecycle_state === "archived" ? "slate" : "green"}>{row.lifecycle_state || "active"}</Tag>;
  }
  if (k === "status" && row.role !== "client" && row.role !== "vendor") {
    if (row.status !== "inactive" && row.last_working_day) {
      return <Tag tone="yellow" data-testid={`registry-employment-status-${row.id}`}>on notice</Tag>;
    }
    const s = row.status === "inactive" ? "inactive" : "active";
    return <Tag tone={s === "inactive" ? "red" : "green"} data-testid={`registry-employment-status-${row.id}`}>{s}</Tag>;
  }
  if (k === "seat_cost_monthly") {
    if (row.seat_cost_monthly == null) return <span className="text-slate-300">—</span>;
    return (
      <button
        onClick={() => onShowCostHistory?.(row)}
        className="inline-flex items-center gap-1 font-mono text-[11px] text-slate-900 hover:text-[#F54800]"
        title="View cost history"
        data-testid={`registry-seat-cost-${row.id}`}
      >
        ₹{Number(row.seat_cost_monthly).toLocaleString("en-IN")}<History size={10} className="text-slate-400" />
      </button>
    );
  }
  if (k === "candidate_stage") {
    const s = row.candidate_stage;
    if (!s) return <span className="text-slate-300">—</span>;
    const tone = s === "hired" ? "green" : s === "rejected" ? "red" : "blue";
    return <Tag tone={tone}>{s}</Tag>;
  }
  // Statutory: show plaintext if revealed, else masked
  const plain = ["pan", "aadhaar", "bank_account", "passport", "driving_licence"];
  if (plain.includes(k)) {
    const v = reveal?.[k];
    if (v) {
      return <span className="font-mono text-[11px] text-slate-900 bg-yellow-50 px-1.5 py-0.5 rounded-sm">{v}</span>;
    }
    return (
      <span className="font-mono text-[11px] text-slate-500 inline-flex items-center gap-1">
        <Lock size={9} className="text-slate-300" />{row[`${k}_masked`]}
      </span>
    );
  }
  const v = row[k];
  if (v == null || v === "") return <span className="text-slate-300">—</span>;
  return <span className="truncate">{String(v)}</span>;
}

const COLS_BY_TAB = {
  employees: [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "role", label: "Role" },
    { key: "phone", label: "Phone" },
    { key: "title", label: "Designation" },
    { key: "status", label: "Employment" },
    { key: "joining_date", label: "Start date" },
    { key: "last_working_day", label: "Last working day" },
    { key: "seat_cost_monthly", label: "Seat Cost/mo" },
    { key: "pan", label: "PAN" },
    { key: "aadhaar", label: "Aadhaar" },
    { key: "bank_account", label: "Bank A/c" },
    { key: "ifsc", label: "IFSC" },
    { key: "lifecycle_state", label: "Status" },
  ],
  clients: [
    { key: "name", label: "Name" },
    { key: "company", label: "Company" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "country", label: "Country" },
    { key: "gstin", label: "GSTIN" },
    { key: "lifecycle_state", label: "Status" },
  ],
  vendors: [
    { key: "company", label: "Company" },
    { key: "name", label: "Contact" },
    { key: "email", label: "Email" },
    { key: "vendor_type", label: "Vendor Type" },
    { key: "category", label: "Category" },
    { key: "phone", label: "Phone" },
    { key: "country", label: "Country" },
    { key: "gstin", label: "GSTIN" },
    { key: "ifsc", label: "IFSC" },
    { key: "bank_account", label: "Bank A/c" },
    { key: "lifecycle_state", label: "Status" },
  ],
  candidates: [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "candidate_role", label: "Role" },
    { key: "candidate_stage", label: "Stage" },
    { key: "candidate_source", label: "Source" },
    { key: "lifecycle_state", label: "Status" },
  ],
};

/* -------------------------------------------------------------------------- */

function RegistryEditDialog({ tab, row, canEditEmployment, isFounder, onClose, onSaved }) {
  const isNew = !row?.id;
  const [form, setForm] = useState(() => ({
    name: row?.name || "",
    email: row?.email || "",
    company: row?.company || "",
    role: row?.role || (tab === "employees" ? "employee" : ""),
    phone: row?.phone || "",
    title: row?.title || "",
    notes: row?.notes || "",
    country: row?.country || "",
    category: row?.category || "",
    vendor_type: row?.vendor_type || "",
    candidate_role: row?.candidate_role || "",
    candidate_stage: row?.candidate_stage || "applied",
    candidate_source: row?.candidate_source || "",
    candidate_cv_url: row?.candidate_cv_url || "",
    // Employment lifecycle — employees tab only
    status: row?.status === "inactive" ? "inactive" : "active",
    joining_date: row?.joining_date || "",
    last_working_day: row?.last_working_day || "",
    date_of_birth: row?.date_of_birth || "",
    celebration_media_url: row?.celebration_media_url || "",
    seat_cost_monthly: row?.seat_cost_monthly ?? "",
    seat_cost_effective_from: "",
    // Statutory (always blank on open — we never echo plaintext)
    pan: "",
    aadhaar: "",
    bank_account: "",
    passport: "",
    driving_licence: "",
    gstin: row?.gstin || "",
    ifsc: row?.ifsc || "",
    email_muted: row?.email_muted || false,
  }));
  const [saving, setSaving] = useState(false);
  const [designations, setDesignations] = useState([]);
  const [vendorTypes, setVendorTypes] = useState([]);

  useEffect(() => {
    if (tab === "employees") api.get("/taxonomy/lists/designations").then(({ data }) => setDesignations(data?.values || [])).catch(() => {});
    if (tab === "vendors") api.get("/taxonomy/lists/vendor_types").then(({ data }) => setVendorTypes(data?.values || [])).catch(() => {});
  }, [tab]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (tab !== "employees") {
        delete payload.status; delete payload.joining_date; delete payload.last_working_day; delete payload.seat_cost_monthly; delete payload.date_of_birth; delete payload.celebration_media_url;
      } else if (!canEditEmployment) {
        // Founders/Admins only — never send fields this user isn't allowed to change.
        delete payload.status; delete payload.last_working_day;
      }
      if (tab !== "employees" || !isFounder) {
        delete payload.seat_cost_monthly; delete payload.seat_cost_effective_from; delete payload.celebration_media_url;
      } else if (payload.seat_cost_monthly === "") {
        delete payload.seat_cost_monthly; delete payload.seat_cost_effective_from;
      } else {
        payload.seat_cost_monthly = Number(payload.seat_cost_monthly);
        if (!payload.seat_cost_effective_from) delete payload.seat_cost_effective_from;
      }
      // Don't send blank statutory values — we don't want to clobber existing encrypted data
      // unless the user explicitly intends to clear (we treat blank as "no change").
      ["pan", "aadhaar", "bank_account", "passport", "driving_licence"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      if (isNew) {
        await api.post(`/registry/${tab}`, payload);
        toast.success("Created");
      } else {
        await api.patch(`/registry/${tab}/${row.id}`, payload);
        toast.success("Updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto grid-cols-1" data-testid="registry-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isNew ? `New ${tab.replace(/s$/, "")}` : `Edit ${tab.replace(/s$/, "")}`}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Statutory IDs (PAN / Aadhaar / Bank A/c / Passport / DL) are encrypted at rest. Leave them blank to keep the existing value unchanged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => update("name", e.target.value)} className="rounded-sm" data-testid="reg-field-name" />
          </Field>
          {tab !== "candidates" && (
            <Field label="Email" required>
              <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="rounded-sm" data-testid="reg-field-email" />
            </Field>
          )}
          {tab === "candidates" && (
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="rounded-sm" data-testid="reg-field-email" />
            </Field>
          )}
          {(tab === "clients" || tab === "vendors") && (
            <Field label="Company">
              <Input value={form.company} onChange={(e) => update("company", e.target.value)} className="rounded-sm" />
            </Field>
          )}
          <Field label="Phone">
            <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="rounded-sm" />
          </Field>
          {(tab === "employees" || tab === "clients") && (
            <Field label="Emails">
              <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-600" data-testid="reg-field-email-muted">
                <Checkbox
                  checked={!!form.email_muted}
                  onCheckedChange={(v) => update("email_muted", !!v)}
                  className="data-[state=checked]:bg-[#F54800] data-[state=checked]:border-[#F54800]"
                />
                Mute all emails for this {tab === "clients" ? "client" : "person"}
              </label>
            </Field>
          )}
          {(tab === "clients" || tab === "vendors") && (
            <Field label="Title">
              <Input value={form.title} onChange={(e) => update("title", e.target.value)} className="rounded-sm" />
            </Field>
          )}
          {tab === "employees" && (
            <Field label="Designation">
              <Select value={form.title || ""} onValueChange={(v) => update("title", v)}>
                <SelectTrigger className="rounded-sm" data-testid="reg-field-designation"><SelectValue placeholder="Pick a designation" /></SelectTrigger>
                <SelectContent>
                  {designations.length === 0 && <div className="px-2 py-1 text-xs text-slate-400">No designations yet — add one in Studio &gt; Taxonomy</div>}
                  {designations.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}

          {tab === "employees" && (
            <Field label="Internal role">
              <Select value={form.role} onValueChange={(v) => update("role", v)}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["admin", "hr", "finance", "client_manager", "designer", "employee"].map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          {tab === "employees" && (
            <>
              <Field label="Employment status" hint={canEditEmployment ? "" : "Only Founders/Admins can change this"}>
                <Select value={form.status} onValueChange={(v) => update("status", v)} disabled={!canEditEmployment}>
                  <SelectTrigger className="rounded-sm" data-testid="reg-field-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Start date">
                <Input type="date" value={form.joining_date} onChange={(e) => update("joining_date", e.target.value)} className="rounded-sm" data-testid="reg-field-start-date" />
              </Field>
              <Field label="Date of birth" hint="Powers the client portal's Studio Pulse birthday shout-out">
                <Input type="date" value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} className="rounded-sm" data-testid="reg-field-dob" />
              </Field>
              <Field label="Last working day" hint={canEditEmployment ? "Set when offboarding" : "Only Founders/Admins can change this"}>
                <Input type="date" value={form.last_working_day} onChange={(e) => update("last_working_day", e.target.value)} className="rounded-sm" disabled={!canEditEmployment} data-testid="reg-field-last-working-day" />
              </Field>
              {isFounder && (
                <>
                  <Field label="Celebration media link" hint="Founder-only. Paste a Google Drive slideshow/GIF/video share link — shown on this person's Birthday/Anniversary hero banner instead of the default GIF.">
                    <Input value={form.celebration_media_url} onChange={(e) => update("celebration_media_url", e.target.value)} className="rounded-sm" placeholder="https://drive.google.com/file/d/…/view" data-testid="reg-field-celebration-media" />
                  </Field>
                  <Field label="Monthly seat cost ₹" hint="Founder-only. Feeds every project P&L resource-cost calc — changes are logged to cost history.">
                    <Input type="number" min="0" value={form.seat_cost_monthly} onChange={(e) => update("seat_cost_monthly", e.target.value)} className="rounded-sm" placeholder="e.g. 85000" data-testid="reg-field-seat-cost" />
                  </Field>
                  <Field label="Effective from" hint="Optional — defaults to today. Backdate a correction so historical P&L stays accurate.">
                    <Input type="date" value={form.seat_cost_effective_from} onChange={(e) => update("seat_cost_effective_from", e.target.value)} className="rounded-sm" data-testid="reg-field-seat-cost-effective-from" />
                  </Field>
                </>
              )}
            </>
          )}
          {tab === "clients" && (
            <Field label="Country">
              <Input value={form.country} onChange={(e) => update("country", e.target.value)} className="rounded-sm" data-testid="reg-field-country" />
            </Field>
          )}
          {tab === "vendors" && (
            <>
              <Field label="Vendor Type">
                <Select value={form.vendor_type || ""} onValueChange={(v) => update("vendor_type", v)}>
                  <SelectTrigger className="rounded-sm" data-testid="reg-field-vendor-type"><SelectValue placeholder="Pick a type" /></SelectTrigger>
                  <SelectContent>
                    {vendorTypes.length === 0 && <div className="px-2 py-1 text-xs text-slate-400">No types yet — add one in Studio &gt; Taxonomy</div>}
                    {vendorTypes.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Category">
                <Input value={form.category} onChange={(e) => update("category", e.target.value)} className="rounded-sm" data-testid="reg-field-category" />
              </Field>
              <Field label="Country">
                <Input value={form.country} onChange={(e) => update("country", e.target.value)} className="rounded-sm" data-testid="reg-field-vendor-country" />
              </Field>
            </>
          )}
          {tab === "candidates" && (
            <>
              <Field label="Role applied">
                <Input value={form.candidate_role} onChange={(e) => update("candidate_role", e.target.value)} className="rounded-sm" />
              </Field>
              <Field label="Stage">
                <Select value={form.candidate_stage} onValueChange={(v) => update("candidate_stage", v)}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Source">
                <Input value={form.candidate_source} onChange={(e) => update("candidate_source", e.target.value)} className="rounded-sm" placeholder="e.g. Referral, LinkedIn, Naukri" />
              </Field>
              <Field label="CV URL" colSpan>
                <Input value={form.candidate_cv_url} onChange={(e) => update("candidate_cv_url", e.target.value)} className="rounded-sm" placeholder="https://…" />
              </Field>
            </>
          )}
        </div>

        {/* Statutory section — hidden for Subscription vendors (Phase 100):
            most SaaS subscriptions have no GSTIN/bank account on file. */}
        {!(tab === "vendors" && (form.vendor_type || "").toLowerCase() === "subscription") && (
        <div className="mt-2" data-testid="registry-statutory-section">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2 inline-flex items-center gap-1.5">
            <Lock size={10} /> Statutory · encrypted at rest
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="PAN" hint={row?.pan_has_value ? "Leave blank to keep current value" : "5 letters + 4 digits + 1 letter"}>
              <Input value={form.pan} onChange={(e) => update("pan", e.target.value.toUpperCase())} className="rounded-sm font-mono uppercase" placeholder="ABCDE1234F" data-testid="reg-field-pan" />
            </Field>
            <Field label="Aadhaar" hint={row?.aadhaar_has_value ? "Leave blank to keep current value" : "12 digits"}>
              <Input value={form.aadhaar} onChange={(e) => update("aadhaar", e.target.value.replace(/\D/g, ""))} maxLength={12} className="rounded-sm font-mono" placeholder="1234 5678 9012" data-testid="reg-field-aadhaar" />
            </Field>
            <Field label="Bank account #" hint={row?.bank_account_has_value ? "Leave blank to keep current value" : ""}>
              <Input value={form.bank_account} onChange={(e) => update("bank_account", e.target.value.replace(/\D/g, ""))} className="rounded-sm font-mono" data-testid="reg-field-bank" />
            </Field>
            <Field label="IFSC (visible)">
              <Input value={form.ifsc} onChange={(e) => update("ifsc", e.target.value.toUpperCase())} className="rounded-sm font-mono uppercase" placeholder="HDFC0000123" />
            </Field>
            <Field label="GSTIN (visible)">
              <Input value={form.gstin} onChange={(e) => update("gstin", e.target.value.toUpperCase())} className="rounded-sm font-mono uppercase" placeholder="27ABCDE1234F1Z5" />
            </Field>
            <Field label="Passport #" hint={row?.passport_has_value ? "Leave blank to keep current value" : ""}>
              <Input value={form.passport} onChange={(e) => update("passport", e.target.value.toUpperCase())} className="rounded-sm font-mono uppercase" />
            </Field>
            <Field label="Driving licence" hint={row?.driving_licence_has_value ? "Leave blank to keep current value" : ""}>
              <Input value={form.driving_licence} onChange={(e) => update("driving_licence", e.target.value.toUpperCase())} className="rounded-sm font-mono uppercase" />
            </Field>
          </div>
        </div>
        )}

        <Field label="Notes">
          <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} className="rounded-sm" />
        </Field>

        {tab === "clients" && !isNew && (
          <ClientPortalCustomizationSection clientId={row.id} initial={row.portal_customization} />
        )}
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="registry-save">
            {saving ? <><Loader2 size={11} className="animate-spin mr-1.5" /> Saving…</> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClientQuotePoolPanel() {
  const [open, setOpen] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newText, setNewText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/client-quotes"); setQuotes(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const addQuote = async () => {
    if (!newText.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/client-quotes", { text: newText.trim() });
      setNewText("");
      toast.success("Added to the client rotation pool");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const removeQuote = async (id) => {
    if (!window.confirm("Remove this quote from the rotation pool?")) return;
    try {
      await api.delete(`/admin/client-quotes/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="card-flat p-4" data-testid="client-quote-pool-panel">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
        data-testid="client-quote-pool-toggle"
      >
        <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1.5">
          <Quote size={12} className="text-[#F54800]" /> Client Hero — rotating quote pool ({quotes.length || "…"})
        </span>
        <span className="text-xs text-slate-400">{open ? "Hide" : "Manage"}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="text-[11px] text-slate-400">
            Shared across every client's Hero Bar — one principle rotates per day. Founders can grow this pool over time; a client's pinned quote (set below, per client) always overrides the rotation for them.
          </div>
          <div className="flex gap-2">
            <Input
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder="Add a new studio-management principle…"
              className="rounded-sm text-xs"
              data-testid="client-quote-pool-new-input"
            />
            <Button size="sm" onClick={addQuote} disabled={saving || !newText.trim()} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white shrink-0" data-testid="client-quote-pool-add">
              <Plus size={13} className="mr-1" /> Add
            </Button>
          </div>
          {loading ? (
            <div className="text-xs text-slate-400 py-3 text-center"><Loader2 size={12} className="animate-spin inline mr-1.5" /> Loading…</div>
          ) : (
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-sm">
              {quotes.map((q) => (
                <div key={q.id} className="flex items-start gap-2 px-3 py-2" data-testid={`client-quote-pool-row-${q.id}`}>
                  <span className="text-xs text-slate-700 flex-1">{q.text}</span>
                  <button onClick={() => removeQuote(q.id)} className="text-slate-300 hover:text-red-500 shrink-0" data-testid={`client-quote-pool-remove-${q.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ClientPortalCustomizationSection({ clientId, initial }) {
  const [quote, setQuote] = useState(initial?.quote_override || "");
  const [shoutout, setShoutout] = useState(initial?.shoutout || "");
  const [vis, setVis] = useState({ holidays: true, birthdays: true, anniversaries: true, shoutouts: true, ...(initial?.widget_visibility || {}) });
  const [nudgesEnabled, setNudgesEnabled] = useState(initial?.nudges_enabled !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/clients/${clientId}/portal-customization`, {
        quote_override: quote.trim() || null,
        shoutout: shoutout.trim() || null,
        widget_visibility: vis,
        nudges_enabled: nudgesEnabled,
      });
      toast.success("Portal customization saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const WIDGETS = [["holidays", "Holidays"], ["birthdays", "Birthdays"], ["anniversaries", "Anniversaries"], ["shoutouts", "Shout-outs"]];

  return (
    <div className="mt-2 border border-slate-200 rounded-sm p-4 bg-slate-50/60" data-testid="client-portal-customization">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-3 inline-flex items-center gap-1.5">
        <Sparkles size={10} /> Portal customization — /client Hero Bar
      </div>
      <div className="grid grid-cols-1 gap-3">
        <Field label="Pinned quote" hint="Overrides today's rotating quote for this client only. Leave blank to use the daily rotation.">
          <Textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={2} className="rounded-sm" placeholder="e.g. If you want to go far, go together." data-testid="portal-custom-quote" />
        </Field>
        <Field label="Team highlight / shout-out" hint="One-line manual celebration shown in Studio Pulse. Leave blank to hide.">
          <Input value={shoutout} onChange={(e) => setShoutout(e.target.value)} className="rounded-sm" placeholder="e.g. CAD Phase finished with 0 errors!" data-testid="portal-custom-shoutout" />
        </Field>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Visible widgets</Label>
          <div className="flex flex-wrap gap-3 mt-1.5">
            {WIDGETS.map(([key, label]) => (
              <label key={key} className="text-xs text-slate-600 inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={!!vis[key]} onChange={(e) => setVis((v) => ({ ...v, [key]: e.target.checked }))} className="rounded-sm" data-testid={`portal-custom-toggle-${key}`} />
                {label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Nudge bar</Label>
          <label className="text-xs text-slate-600 inline-flex items-center gap-1.5 cursor-pointer mt-1.5">
            <input type="checkbox" checked={nudgesEnabled} onChange={(e) => setNudgesEnabled(e.target.checked)} className="rounded-sm" data-testid="portal-custom-toggle-nudges" />
            Enable Nudges (approval reminders + monthly Google Review prompt)
          </label>
        </div>
      </div>
      <Button size="sm" onClick={save} disabled={saving} className="rounded-sm h-8 mt-3 bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="portal-custom-save">
        {saving ? <><Loader2 size={11} className="animate-spin mr-1.5" /> Saving…</> : "Save customization"}
      </Button>
    </div>
  );
}

function Field({ label, hint, required, colSpan, children }) {
  return (
    <div className={colSpan ? "col-span-2" : ""}>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}{required && <span className="text-[#F54800]"> *</span>}
      </Label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function SeatCostHistoryDialog({ row, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/registry/employees/${row.id}/seat-cost-history`)
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [row.id]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-lg grid-cols-1" data-testid="seat-cost-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display inline-flex items-center gap-2"><History size={16} /> Seat cost history — {row.name}</DialogTitle>
          <DialogDescription className="text-xs">Every change to Monthly Seat Cost is logged here so past project P&Ls stay accurate to the cost active at the time.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="p-6 text-center text-xs text-slate-400"><Loader2 size={12} className="animate-spin inline mr-1.5" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-xs text-slate-400">No cost recorded yet.</div>
        ) : (
          <div className="overflow-hidden rounded-sm border border-slate-200">
            <table className="w-full text-xs" data-testid="seat-cost-history-table">
              <thead className="bg-slate-50">
                <tr>
                  {["Effective from", "Effective to", "Monthly cost", "Changed by"].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-medium text-slate-500 uppercase tracking-wider text-[10px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2">{r.effective_from}</td>
                    <td className="px-3 py-2">{r.effective_to || <span className="text-green-700 font-medium">ongoing</span>}</td>
                    <td className="px-3 py-2 font-mono">₹{Number(r.monthly_cost).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-slate-500">{r.created_by_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
